import React from "react";
import { useNavigate } from "react-router-dom";
import { useDoctorContext } from "../../Navigation/context/DoctorContext";
import { BottomNav } from "../../Common/custom_components/bottom_nav/BottomNav";
import { useHistoryModule } from "./hooks/history_module.hook";
import { FilterType } from "./interfaces/history_module.interface";
import style from "./history.module.css";

const formatDuration = (seconds?: number) => {
  if (!seconds) return "—";
  return `${Math.round(seconds / 60)} min`;
};

const FILTERS: { key: FilterType; label: string }[] = [
  { key: "all", label: "All" },
  { key: "completed", label: "Completed" },
  { key: "cancelled", label: "Cancelled" },
  { key: "week", label: "This Week" },
  { key: "month", label: "This Month" },
];

export const DoctorHistory: React.FC = () => {
  const navigate = useNavigate();
  const { doctorProfile } = useDoctorContext();
  const {
    isLoading,
    groups,
    total,
    page,
    limit,
    search,
    activeFilter,
    completedCount,
    avgDuration,
    setSearch,
    setActiveFilter,
    setPage,
  } = useHistoryModule();

  return (
    <div className={style.doctorBody}>
      {/* Header */}
      <div className={style.docHeader}>
        <div className={style.docHeaderLeft}>
          <img
            src="/logo-transparent3.png"
            alt="Pococare"
            style={{ height: "56px" }}
            onError={(e) => {
              (e.target as HTMLImageElement).src =
                "https://nice-stone-091e25e00.3.azurestaticapps.net/logo-transparent3.png";
            }}
          />
          <div className={style.docHeaderInfo} style={{ marginLeft: "12px" }}>
            <h1>Consultation History</h1>
            <span>{doctorProfile?.name ? `Dr. ${doctorProfile.name}` : ""}</span>
          </div>
        </div>
      </div>

      {/* Stats Row */}
      <div className={style.historyStatsRow}>
        <div className={style.historyStatCard}>
          <div className={style.statNumber}>{total}</div>
          <div className={style.statLabel}>Total</div>
        </div>
        <div className={style.historyStatCard}>
          <div className={style.statNumber}>{completedCount}</div>
          <div className={style.statLabel}>Completed</div>
        </div>
        <div className={style.historyStatCard}>
          <div className={style.statNumber}>{avgDuration > 0 ? `${avgDuration}m` : "—"}</div>
          <div className={style.statLabel}>Avg Duration</div>
        </div>
      </div>

      {/* Search */}
      <div style={{ padding: "12px 16px 4px" }}>
        <div className={style.historySearchBar}>
          <span className="material-icons-round">search</span>
          <input
            type="text"
            placeholder="Search by patient or ticket ID..."
            value={search}
            onChange={(e) => setSearch(e.target.value)}
          />
        </div>
      </div>

      {/* Filter Chips */}
      <div className={style.historyFiltersRow}>
        {FILTERS.map(({ key, label }) => (
          <span
            key={key}
            className={`${style.historyFilterChip} ${activeFilter === key ? style.active : ""}`}
            onClick={() => setActiveFilter(key)}
          >
            {label}
          </span>
        ))}
      </div>

      {/* List */}
      <div style={{ padding: "0 16px", paddingBottom: "100px" }}>
        {isLoading ? (
          <div style={{ textAlign: "center", padding: "40px", color: "var(--primary)", fontWeight: 600 }}>
            Loading...
          </div>
        ) : groups.length === 0 ? (
          <div style={{
            textAlign: "center", padding: "40px", background: "#fff",
            borderRadius: "16px", color: "var(--placeholder)", marginTop: "12px",
          }}>
            No consultations found.
          </div>
        ) : (
          groups.map((group) => (
            <div key={group.label}>
              <div className={style.historyDateHeader}>{group.label}</div>
              {group.items.map((item) => {
                const initials = item.patient_name
                  ? item.patient_name.split(" ").map((w) => w[0]).join("").slice(0, 2).toUpperCase()
                  : "PT";
                return (
                  <div
                    key={item._id}
                    className={style.historyConsultationItem}
                    onClick={() => navigate(`/consultation/${item._id}/detail`)}
                  >
                    <div className={style.historyItemAvatar}>{initials}</div>
                    <div className={style.historyItemInfo}>
                      <div className={style.historyItemName}>{item.patient_name || "Patient"}</div>
                      <div className={style.historyItemMeta}>
                        <span>{String(item.ticket_id || "N/A")}</span>
                        <span className={style.dot} />
                        <span style={{ textTransform: "capitalize" }}>{item.consultation_mode || "Video"}</span>
                      </div>
                    </div>
                    <div className={style.historyItemRight}>
                      <span className={style.historyItemDuration}>{formatDuration(item.call_duration_seconds)}</span>
                      <span className={`${style.historyItemStatus} ${style[item.status]}`}>
                        {item.status === "completed" ? "Completed" : "Cancelled"}
                      </span>
                    </div>
                  </div>
                );
              })}
            </div>
          ))
        )}

        {/* Pagination */}
        {!isLoading && total > limit && (
          <div className={style.historyPagination}>
            <button className={style.btnPage} onClick={() => setPage((p) => p - 1)} disabled={page === 1}>
              Previous
            </button>
            <span className={style.pageNum}>Page {page} of {Math.ceil(total / limit)}</span>
            <button className={style.btnPage} onClick={() => setPage((p) => p + 1)} disabled={page * limit >= total}>
              Next
            </button>
          </div>
        )}
      </div>

      <BottomNav />
    </div>
  );
};
